import * as R from 'ramda';
import styled from 'styled-components';
import Popover from '@mui/material/Popover';
import { OuterClick } from 'react-outer-click';
import React, { useRef, useState, useEffect, useCallback } from 'react';
// helpers/constants
import * as G from '../helpers';
//////////////////////////////////////////////////

export const InnerPopover = styled.div`
  right: 5px;
  display: flex;
  padding: 5px 0;
  margin-top: 4px;
  user-select: none;
  align-items: start;
  position: relative;
  word-break: break-all;
  flex-direction: column;
  justify-content: space-between;
  background: ${G.getTheme('colors.white')};
  border-radius: ${({ $borderRadius }) => $borderRadius || '2px'};
  box-shadow: 0px 1px 8px 0px ${G.getTheme('colors.boxShadowGrey')};
`;

const defaultAnchorOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};

const defaultTransformOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

const useFixedPopover = (props = {}) => {
  const {
    popoverOverflow,
    shouldNotCloseOnScroll,
  } = props;

  const [opened, setOpened] = useState(false);
  const [component, setComponent] = useState(null);
  const activePopoverId = useRef(null);

  const closeFixedPopup = useCallback((popoverId) => {
    if (R.and(
      R.is(String, popoverId),
      R.not(R.equals(popoverId, activePopoverId.current)),
    )) return;

    activePopoverId.current = null;
    setComponent(null);
    setOpened(false);
    // eslint-disable-next-line no-use-before-define
    removeScrollHandler();
  }, []);

  const removeScrollHandler = useCallback(
    () => window.removeEventListener('scroll', closeFixedPopup, true),
    [closeFixedPopup],
  );

  useEffect(() => () => removeScrollHandler(), [removeScrollHandler]);

  const openFixedPopup = useCallback((props) => {
    const {
      el,
      zIndex,
      content,
      popoverId,
      anchorOrigin,
      transformOrigin,
      popoverBorderRadius,
    } = props;

    if (R.not(shouldNotCloseOnScroll)) {
      window.addEventListener('scroll', closeFixedPopup, true);
    }

    activePopoverId.current = R.or(popoverId, null);

    const PopupComponent = (
      <Popover
        open={true}
        anchorEl={el}
        PaperProps={{
          style: {
            borderRadius: 0,
            overflow: 'unset',
            boxShadow: 'none',
            pointerEvents: 'all',
            backgroundColor: 'transparent',
          },
        }}
        hideBackdrop={true}
        onClose={closeFixedPopup}
        style={{ zIndex, pointerEvents: 'none' }}
        anchorOrigin={R.or(anchorOrigin, defaultAnchorOrigin)}
        transformOrigin={R.or(transformOrigin, defaultTransformOrigin)}
      >
        <OuterClick onOuterClick={closeFixedPopup} id='fixed-popover-wrapper'>
          <InnerPopover $borderRadius={popoverBorderRadius}>
            {content}
          </InnerPopover>
        </OuterClick>
      </Popover>
    );

    setComponent(PopupComponent);
    setOpened(true);
  }, [closeFixedPopup, shouldNotCloseOnScroll]);

  return {
    openFixedPopup,
    closeFixedPopup,
    PopoverComponent: (
      <div style={{ overflow: R.or(popoverOverflow, 'unset') }}>
        {opened ? component : null}
      </div>
    ),
  };
};

export default useFixedPopover;
